import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetMyFilesResourceAccessUrlMutation } from "@/features/my-files/myFilesApi";

interface CachedAccessURL {
  expiresAt: number;
  url: string;
}

const accessURLCache = new Map<string, CachedAccessURL>();
const pendingAccessURLs = new Map<string, Promise<CachedAccessURL>>();
const minimumLifetimeMs = 30_000;

function cacheKey(organizationId: string, resourceId: string) {
  return `${organizationId}:${resourceId}`;
}

function currentCachedURL(key: string) {
  const cached = accessURLCache.get(key);
  if (!cached || cached.expiresAt - Date.now() <= minimumLifetimeMs) {
    accessURLCache.delete(key);
    return null;
  }
  return cached;
}

export function useCreativeMediaAccessUrl(
  organizationId: string,
  resourceId: string,
) {
  const key = useMemo(
    () => cacheKey(organizationId, resourceId),
    [organizationId, resourceId],
  );
  const [requestAccessURL] = useGetMyFilesResourceAccessUrlMutation();
  const [value, setValue] = useState<CachedAccessURL | null>(() =>
    currentCachedURL(key),
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(() => value === null);

  const load = useCallback(
    async (force = false) => {
      if (!organizationId || !resourceId) return;
      const cached = force ? null : currentCachedURL(key);
      if (cached) {
        setValue(cached);
        setError(null);
        setLoading(false);
        return;
      }
      if (force) accessURLCache.delete(key);
      setLoading(true);
      setError(null);
      let pending = pendingAccessURLs.get(key);
      if (!pending) {
        pending = requestAccessURL({ organizationId, resourceId })
          .unwrap()
          .then((response) => ({
            expiresAt: Date.parse(response.expiresAt),
            url: response.url,
          }))
          .then((next) => {
            accessURLCache.set(key, next);
            return next;
          })
          .finally(() => pendingAccessURLs.delete(key));
        pendingAccessURLs.set(key, pending);
      }
      try {
        setValue(await pending);
      } catch {
        setValue(null);
        setError("媒体暂时无法读取");
      } finally {
        setLoading(false);
      }
    },
    [key, organizationId, requestAccessURL, resourceId],
  );

  useEffect(() => {
    let canceled = false;
    queueMicrotask(() => {
      if (!canceled) void load();
    });
    return () => {
      canceled = true;
    };
  }, [load]);

  return {
    error,
    loading,
    retry: () => void load(true),
    url: value?.url ?? "",
  };
}

export function clearCreativeMediaAccessURLCache() {
  accessURLCache.clear();
  pendingAccessURLs.clear();
}
